import { useCallback, useMemo } from 'react'
import { useHistory, useLocation } from 'react-router'
import queryString from 'query-string'

export const PAGE_SIZE_OPTIONS = [10, 20, 50] as const

const DEFAULT_PAGE = 1
const DEFAULT_PAGE_SIZE = 20

const parsePositiveInt = (value: string | string[] | null | undefined, fallback: number) => {
  const raw = Array.isArray(value) ? value[0] : value
  const parsed = Number(raw)

  return Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : fallback
}

const normalizePageSize = (pageSize: number) =>
  PAGE_SIZE_OPTIONS.includes(pageSize as typeof PAGE_SIZE_OPTIONS[number]) ? pageSize : DEFAULT_PAGE_SIZE

export const usePaginationLocation = () => {
  const location = useLocation()
  const history = useHistory()
  const queryObject = useMemo(() => queryString.parse(location.search), [location.search])

  const getFromUrl = useCallback(() => {
    const page = parsePositiveInt(queryObject.page, DEFAULT_PAGE)
    const pageSize = normalizePageSize(parsePositiveInt(queryObject.pageSize, DEFAULT_PAGE_SIZE))

    return { page, pageSize }
  }, [queryObject.page, queryObject.pageSize])

  const updateSearch = useCallback(
    (updates: { page?: number; pageSize?: number }) => {
      const searchParams = new URLSearchParams(history.location.search)

      if (updates.page !== undefined) {
        searchParams.set('page', String(updates.page))
      }

      if (updates.pageSize !== undefined) {
        searchParams.set('pageSize', String(updates.pageSize))
        searchParams.set('page', '1')
      }

      history.push({ search: searchParams.toString() })
    },
    [history],
  )

  const setPage = useCallback((page: number) => updateSearch({ page }), [updateSearch])

  const setPageSize = useCallback((pageSize: number) => updateSearch({ pageSize }), [updateSearch])

  const ensureInUrl = useCallback(() => {
    const searchParams = new URLSearchParams(history.location.search)
    let changed = false

    if (!searchParams.get('page')) {
      searchParams.set('page', String(DEFAULT_PAGE))
      changed = true
    }

    if (!searchParams.get('pageSize')) {
      searchParams.set('pageSize', String(DEFAULT_PAGE_SIZE))
      changed = true
    }

    if (changed) {
      history.replace({ search: searchParams.toString() })
    }
  }, [history])

  return useMemo(() => ({ getFromUrl, setPage, setPageSize, ensureInUrl }), [ensureInUrl, getFromUrl, setPage, setPageSize])
}
